import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from '@/hooks/use-toast';

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3070/api';

// CSRF token storage
let csrfToken: string | null = null;

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies
  timeout: 30000,
});

// Request interceptor to add CSRF token
api.interceptors.request.use(
  async (config) => {
    // Add CSRF token to all non-GET requests
    if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
      if (!csrfToken) {
        // Fetch CSRF token if we don't have one
        await fetchCSRFToken();
      }
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Extended request config with retry flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

    // If CSRF token is invalid, fetch new one and retry
    if (error.response?.status === 403 && error.response?.data &&
        (error.response.data as { error?: string }).error?.includes('CSRF')) {
      // Reset CSRF token
      csrfToken = null;
      
      // Fetch new CSRF token
      await fetchCSRFToken();
      
      // Retry original request with new token
      if (csrfToken && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        originalRequest.headers.set('X-CSRF-Token', csrfToken);
        return api(originalRequest);
      }
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Clear any stored user data and redirect to login
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/') {
        toast({
          title: 'Session Expired',
          description: 'Please login again to continue.',
          variant: 'destructive',
        });
        // Trigger logout in auth context
        window.dispatchEvent(new CustomEvent('auth:logout'));
        window.location.href = '/login';
      }
    }

    // Handle network errors
    if (!error.response) {
      toast({
        title: 'Network Error',
        description: 'Unable to connect to the server. Please check your connection.',
        variant: 'destructive',
      });
    }

    return Promise.reject(error);
  }
);

// Fetch CSRF token from server
async function fetchCSRFToken() {
  try {
    const response = await axios.get(`${API_BASE_URL}/auth/csrf-token`, {
      withCredentials: true,
    });
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
}

// Auth API endpoints
export const authAPI = {
  async login(email: string, password: string, rememberMe: boolean = false) {
    const response = await api.post('/auth/login', { email, password, rememberMe });
    // Store CSRF token from login response
    if (response.data.csrfToken) {
      csrfToken = response.data.csrfToken;
    }
    return response.data;
  },

  async register(email: string, password: string, name: string, organizationName?: string) {
    const response = await api.post('/auth/register', { 
      email, 
      password, 
      name, 
      organizationName 
    });
    // Store CSRF token from register response
    if (response.data.csrfToken) {
      csrfToken = response.data.csrfToken;
    }
    return response.data;
  },

  async logout() {
    const response = await api.post('/auth/logout');
    csrfToken = null; // Clear CSRF token on logout
    return response.data;
  },

  async logoutAll() {
    const response = await api.post('/auth/logout-all');
    csrfToken = null;
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async getSessions() {
    const response = await api.get('/auth/sessions');
    return response.data;
  },

  async refreshSession() {
    const response = await api.post('/auth/refresh');
    if (response.data.csrfToken) {
      csrfToken = response.data.csrfToken;
    }
    return response.data;
  },

  async switchOrganization(organizationId: string) {
    const response = await api.post('/auth/switch-organization', { organizationId });
    return response.data;
  },

  async forgotPassword(email: string) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(token: string, password: string) {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

// User API endpoints
export const userAPI = {
  async getUsers() {
    const response = await api.get('/users');
    return response.data;
  },

  async getUser(id: string) {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  async updateUser(id: string, data: { name?: string; email?: string; role?: string; avatar?: string }) {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string) {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  async updateProfile(data: { firstName?: string; lastName?: string; phone?: string; preferences?: Record<string, unknown> }) {
    const response = await api.patch('/users/profile', data);
    return response.data;
  },

  async uploadAvatar(file: File) {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.post('/users/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Tenant/Organization API endpoints
export const tenantAPI = {
  async getTenant(id: string) {
    const response = await api.get(`/tenants/${id}`);
    return response.data;
  },

  async updateTenant(id: string, data: { name?: string; logo?: string; settings?: Record<string, unknown> }) {
    const response = await api.patch(`/tenants/${id}`, data);
    return response.data;
  },

  async getMembers(tenantId: string) {
    const response = await api.get(`/tenants/${tenantId}/members`);
    return response.data;
  },

  async inviteMember(tenantId: string, email: string, role: string) {
    const response = await api.post(`/tenants/${tenantId}/invite`, { email, role });
    return response.data;
  },

  async removeMember(tenantId: string, userId: string) {
    const response = await api.delete(`/tenants/${tenantId}/members/${userId}`);
    return response.data;
  },

  async updateMemberRole(tenantId: string, userId: string, role: string) {
    const response = await api.patch(`/tenants/${tenantId}/members/${userId}`, { role });
    return response.data;
  },

  async getBilling(tenantId: string) {
    const response = await api.get(`/tenants/${tenantId}/billing`);
    return response.data;
  },

  async updateBilling(tenantId: string, data: { paymentMethod?: Record<string, unknown>; billingAddress?: Record<string, string> }) {
    const response = await api.patch(`/tenants/${tenantId}/billing`, data);
    return response.data;
  },
};

// Shot/Cricket API endpoints
export const shotAPI = {
  async getShots() {
    const response = await api.get('/shots');
    return response.data;
  },

  async getShot(id: string) {
    const response = await api.get(`/shots/${id}`);
    return response.data;
  },

  async createShot(data: { sessionId: string; playerId: string; shotType: string; ballSpeed: number; batSpeed: number; distance: number; [key: string]: unknown }) {
    const response = await api.post('/shots', data);
    return response.data;
  },

  async updateShot(id: string, data: { quality?: string; notes?: string; tags?: string[] }) {
    const response = await api.patch(`/shots/${id}`, data);
    return response.data;
  },

  async deleteShot(id: string) {
    const response = await api.delete(`/shots/${id}`);
    return response.data;
  },

  async getShotAnalytics(id: string) {
    const response = await api.get(`/shots/${id}/analytics`);
    return response.data;
  },

  async getSessions() {
    const response = await api.get('/shots/sessions');
    return response.data;
  },

  async getSession(sessionId: string) {
    const response = await api.get(`/shots/sessions/${sessionId}`);
    return response.data;
  },

  async createSession(data: { title: string; playerId: string; description?: string; scheduledStart?: string }) {
    const response = await api.post('/shots/sessions', data);
    return response.data;
  },

  async endSession(sessionId: string) {
    const response = await api.post(`/shots/sessions/${sessionId}/end`);
    return response.data;
  },
};

// Analytics API endpoints
export const analyticsAPI = {
  async getPlayerAnalytics(playerId: string, timeRange: string = '30d') {
    const response = await api.get(`/analytics/player/${playerId}`, {
      params: { timeRange }
    });
    return response.data;
  },

  async getTeamAnalytics(timeRange: string = '30d') {
    const response = await api.get('/analytics/team', {
      params: { timeRange }
    });
    return response.data;
  },

  async getPlayerTrends(playerId: string, timeRange: string = '30d', metrics: string[] = []) {
    const response = await api.get(`/analytics/player/${playerId}/trends`, {
      params: { timeRange, metrics: metrics.join(',') }
    });
    return response.data;
  },

  async getPlayerComparison(playerId: string, compareWith: string[] = [], timeRange: string = '30d') {
    const response = await api.get(`/analytics/player/${playerId}/comparison`, {
      params: { timeRange, compareWith: compareWith.join(',') }
    });
    return response.data;
  },

  async getPlayerRecommendations(playerId: string) {
    const response = await api.get(`/analytics/player/${playerId}/recommendations`);
    return response.data;
  },

  async getBenchmarkData(ageGroup?: string, skillLevel?: string) {
    const response = await api.get('/analytics/benchmarks', {
      params: { ageGroup, skillLevel }
    });
    return response.data;
  },

  async getCoachingInsights(timeRange: string = '30d') {
    const response = await api.get('/analytics/coaching-insights', {
      params: { timeRange }
    });
    return response.data;
  },

  async getPerformanceMetrics(playerId?: string, timeRange: string = '30d') {
    const endpoint = playerId ? `/analytics/player/${playerId}/metrics` : '/analytics/metrics';
    const response = await api.get(endpoint, {
      params: { timeRange }
    });
    return response.data;
  },

  async exportAnalytics(type: 'player' | 'team', id?: string, format: 'pdf' | 'excel' | 'csv' = 'pdf', timeRange: string = '30d') {
    const endpoint = type === 'player' && id ? 
      `/analytics/export/player/${id}` : 
      '/analytics/export/team';
    
    const response = await api.get(endpoint, {
      params: { format, timeRange },
      responseType: 'blob'
    });
    return response.data;
  },

  async updatePlayerGoals(playerId: string, goals: Array<{ id?: string; type: string; targetValue: number; deadline: string }>) {
    const response = await api.put(`/analytics/player/${playerId}/goals`, { goals });
    return response.data;
  },

  async getSessionAnalytics(sessionId: string) {
    const response = await api.get(`/analytics/session/${sessionId}`);
    return response.data;
  }
};

// Coaching API endpoints
export const coachingAPI = {
  async getRecommendations(playerId: string) {
    const response = await api.get(`/coaching/recommendations/${playerId}`);
    return response.data;
  },

  async createGoal(data: {
    playerId: string;
    type: 'technique' | 'power' | 'accuracy' | 'consistency' | 'specific_shot';
    targetMetric: string;
    targetValue: number;
    currentValue?: number;
    deadline: string;
    description?: string;
    priority?: 'low' | 'medium' | 'high';
  }) {
    const response = await api.post('/coaching/goals', data);
    return response.data;
  },

  async updateGoal(goalId: string, data: { targetValue?: number; deadline?: string; priority?: string; status?: string }) {
    const response = await api.patch(`/coaching/goals/${goalId}`, data);
    return response.data;
  },

  async getProgress(params?: {
    playerId?: string;
    goalId?: string;
    status?: 'active' | 'completed' | 'paused' | 'cancelled';
    dateFrom?: string;
    dateTo?: string;
  }) {
    const response = await api.get('/coaching/progress', { params });
    return response.data;
  },

  async submitFeedback(data: {
    playerId: string;
    sessionId?: string;
    type: 'technique' | 'mental' | 'physical' | 'tactical' | 'general';
    rating: number;
    strengths: string[];
    improvements: string[];
    notes: string;
    actionItems?: Array<{
      task: string;
      priority: 'low' | 'medium' | 'high';
      dueDate?: string;
    }>;
  }) {
    const response = await api.post('/coaching/feedback', data);
    return response.data;
  },

  async getFeedbackHistory(playerId: string, timeRange: string = '30d') {
    const response = await api.get(`/coaching/feedback/${playerId}`, {
      params: { timeRange }
    });
    return response.data;
  },

  async getGoals(playerId?: string, status?: string) {
    const params: { playerId?: string; status?: string } = {};
    if (playerId) params.playerId = playerId;
    if (status) params.status = status;

    const response = await api.get('/coaching/goals', { params });
    return response.data;
  },

  async generateTrainingPlan(playerId: string, data: {
    duration: 'weekly' | 'monthly';
    focus?: string[];
    intensity?: 'low' | 'medium' | 'high';
  }) {
    const response = await api.post(`/coaching/training-plan/${playerId}`, data);
    return response.data;
  },

  async getTrainingPlans(playerId?: string) {
    const endpoint = playerId ? `/coaching/training-plans/${playerId}` : '/coaching/training-plans';
    const response = await api.get(endpoint);
    return response.data;
  },

  async updateGoalProgress(goalId: string, progress: number, notes?: string) {
    const response = await api.patch(`/coaching/goals/${goalId}/progress`, {
      progress,
      notes
    });
    return response.data;
  },

  async getPlayerInsights(playerId: string, timeRange: string = '30d') {
    const response = await api.get(`/coaching/insights/${playerId}`, {
      params: { timeRange }
    });
    return response.data;
  }
};

export default api;